import { memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { LazyImage, Typography, Tooltip } from '@frontend/design_system'
import lockIconSrc from 'assets/icons/grey400/grey400_lock_20x20.webp'
import warningIconSrc from 'assets/icons/yellow700/yellow700_warning_20x20.webp'
import redWarningIconSrc from 'assets/icons/red600/red600_warningIcon_20x20.webp'
import greyWarningIconSrc from 'assets/icons/grey200/grey200_warningIcon_20x20.webp'
import { Props } from './types'
import { joinClasses } from 'utils/joinClasses'
import styles from './styles.module.scss'
import { PENDING_CARD_TOOLTIP } from 'texts/pendingForApproval'
import { isMigratedUserSelector } from 'redux/login/selectors'
import { useAppSelector } from 'redux/hooks'

export const MenuItem = memo(
  ({
    iconSrc,
    name,
    activeItem,
    path,
    withSubmenu = false,
    disabled,
    incomplete,
    requested,
  }: Props) => {
    const navigate = useNavigate()
    const isMigratedUser = useAppSelector(isMigratedUserSelector)

    const isMenuItemActive = activeItem === name

    const handleClick = () => {
      if (path && !withSubmenu) {
        navigate(path)
      }
    }

    const getLabelColor = () => {
      if (disabled) {
        return 'grey400'
      }
      if (isMenuItemActive) {
        return 'blue700'
      }
      return 'blackMaster'
    }

    const renderRightIcon = () => {
      if (disabled) {
        if (isMigratedUser) {
          return incomplete ? (
            <LazyImage
              isPriority
              width={20}
              height={20}
              src={greyWarningIconSrc}
            />
          ) : null
        }

        return <LazyImage isPriority width={20} height={20} src={lockIconSrc} />
      }
      if (requested || incomplete) {
        return (
          <div
            className={joinClasses(styles['warning-icon'], [
              styles.active,
              isMenuItemActive,
            ])}
            data-testid="warning-icon"
          >
            {requested ? (
              <Tooltip
                popupContent={PENDING_CARD_TOOLTIP}
                size="primary"
                placement="bottom"
                alignSelf="center"
              >
                <LazyImage
                  isPriority
                  width={20}
                  height={20}
                  src={redWarningIconSrc}
                />{' '}
              </Tooltip>
            ) : (
              <LazyImage
                isPriority
                width={20}
                height={20}
                src={warningIconSrc}
              />
            )}
          </div>
        )
      }
    }

    return (
      <div>
        <a
          className={joinClasses(
            styles['menu-item'],
            [styles.active, isMenuItemActive],
            [styles['with-submenu'], withSubmenu],
            [styles.disabled, disabled]
          )}
          role="menuitem"
          data-testid="menu-item"
          onClick={!disabled ? handleClick : () => {}}
        >
          <div
            className={styles['menu-item-label']}
            data-testid="menu-item-label"
          >
            <div className={styles.icon}>
              {iconSrc && (
                <LazyImage
                  isPriority
                  width={20}
                  height={20}
                  src={iconSrc}
                  dataTestId="menu-item-icon"
                />
              )}
            </div>
            <Typography
              className={styles.title}
              color={getLabelColor()}
              name="Button2"
              dataTestId="menu-item-title"
            >
              {name}
            </Typography>
          </div>
          {renderRightIcon()}
        </a>
      </div>
    )
  }
)
